import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SyncProvider } from "@/context/SyncContext";
import { LanguageProvider } from "@/lib/i18n";
import { AutoTranslator } from "@/lib/autoTranslator";

import LandingPage from "@/pages/Landing";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import Reactivate from "@/pages/auth/Reactivate";
import DashboardPage from "@/pages/Dashboard";
import CustomersPage from "@/pages/Customers";
import CustomerDetailPage from "@/pages/CustomerDetail";
import VehiclesPage from "@/pages/Vehicles";
import JobCardsPage from "@/pages/job-cards/JobCardsList";
import NewJobCardPage from "@/pages/job-cards/CreateJobCard";
import JobCardDetailPage from "@/pages/job-cards/JobCardDetail";
import MyJobsPage from "@/pages/MyJobs";
import SpareOverview from "@/pages/spare-parts/SpareOverview";
import StockList from "@/pages/spare-parts/StockList";
import PartDetail from "@/pages/spare-parts/PartDetail";
import Vendors from "@/pages/spare-parts/Vendors";
import VendorDetail from "@/pages/spare-parts/VendorDetail";
import ReorderAlerts from "@/pages/spare-parts/ReorderAlerts";
import Purchases from "@/pages/spare-parts/Purchases";
import PurchaseForm from "@/pages/spare-parts/PurchaseForm";
import PurchaseDetail from "@/pages/spare-parts/PurchaseDetail";
import CounterSales from "@/pages/spare-parts/CounterSales";
import CounterSaleDetail from "@/pages/spare-parts/CounterSaleDetail";
import PurchaseReturns, { PurchaseReturnDetail } from "@/pages/spare-parts/PurchaseReturns";
import EnquiriesPage from "@/pages/Enquiries";
import InvoicesList from "@/pages/billing/InvoicesList";
import InvoiceForm from "@/pages/billing/InvoiceForm";
import InvoiceDetail from "@/pages/billing/InvoiceDetail";
import GatePassList from "@/pages/billing/GatePassList";
import GatePassForm from "@/pages/billing/GatePassForm";
import GatePassDetail from "@/pages/billing/GatePassDetail";
import EstimatesList from "@/pages/estimates/EstimatesList";
import CreateEstimate from "@/pages/estimates/CreateEstimate";
import UsersPage from "@/pages/Users";
import ReportsPage from "@/pages/Reports";
import AppointmentsPage from "@/pages/appointments/AppointmentsCalendar";
import PublicJobStatusPage from "@/pages/PublicJobStatus";
import SupportCasesPage from "@/pages/SupportCases";
// Settings module
import SettingsLayout from "@/pages/settings/SettingsLayout";
import GarageProfile from "@/pages/settings/GarageProfile";
import UsersSettings from "@/pages/settings/UsersSettings";
import RolesInfo from "@/pages/settings/RolesInfo";
import DocumentSettings from "@/pages/settings/DocumentSettings";
import Preferences from "@/pages/settings/Preferences";
import DeleteAccount from "@/pages/settings/DeleteAccount";
import ChangePassword from "@/pages/settings/ChangePassword";
// Admin area
import AdminShell from "@/components/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminGarages from "@/pages/admin/AdminGarages";
import AdminGarageProfile from "@/pages/admin/AdminGarageProfile";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminEnquiryDetail from "@/pages/admin/AdminEnquiryDetail";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminMakes from "@/pages/admin/AdminMakes";
import AdminModels from "@/pages/admin/AdminModels";
import AdminServiceTasks from "@/pages/admin/AdminServiceTasks";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminSupportCases from "@/pages/admin/AdminSupportCases";
import AdminSupportCaseDetail from "@/pages/admin/AdminSupportCaseDetail";
import AdminSyncMonitor from "@/pages/admin/AdminSyncMonitor";
import {
  AdminChangeRequests,
  AdminChangeRequestDetail,
} from "@/pages/admin/AdminChangeRequests";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAdoption from "@/pages/admin/AdminAdoption";
import AdminDeletionRequests from "@/pages/admin/AdminDeletionRequests";
import AdminNotificationTemplates from "@/pages/admin/AdminNotificationTemplates";
import AdminJobCards from "@/pages/admin/AdminJobCards";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminEstimates from "@/pages/admin/AdminEstimates";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import AdminJobCardDetail from "@/pages/admin/detail/AdminJobCardDetail";
import AdminEstimateDetail from "@/pages/admin/detail/AdminEstimateDetail";
import AdminInvoiceDetail from "@/pages/admin/detail/AdminInvoiceDetail";
import AdminSpareParts from "@/pages/admin/AdminSpareParts";
import AdminPartCategories from "@/pages/admin/AdminPartCategories";
import { PlatformSettingsProvider } from "@/context/PlatformSettingsContext";
import AppShell from "@/components/AppShell";
import About from "@/pages/legal/About";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Legal from "@/pages/legal/Legal";
import AccountDeletion from "@/pages/legal/AccountDeletion";
import PartsCatalogue from "@/pages/PartsCatalogue";

const Protected = ({ children, adminOnly = false, workshopOnly = false }) => {
  const { status, user } = useAuth();
  const loc = useLocation();
  if (status === "loading") {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-blueprint">
        <div className="text-amber-500 font-mono text-sm">Loading Fender…</div>
      </div>
    );
  }
  if (status !== "authed") {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  const isAdmin = user?.role === "sh_admin";
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (workshopOnly && isAdmin) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const Anon = ({ children }) => {
  const { status, user } = useAuth();
  if (status === "authed") {
    return <Navigate to={user?.role === "sh_admin" ? "/admin/dashboard" : "/dashboard"} replace />;
  }
  return children;
};

function App() {
  React.useEffect(() => {
    if (!("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") return;
    let reloadedOnce = false;
    const sw = navigator.serviceWorker;
    sw.register("/service-worker.js").then((reg) => {
      // When a fresh SW takes over an existing tab, reload once so the user
      // immediately sees the new build without a hard refresh.
      sw.addEventListener("controllerchange", () => {
        if (reloadedOnce) return;
        reloadedOnce = true;
        window.location.reload();
      });
      // If a new SW is already waiting, prompt it to activate now.
      const waiting = reg.waiting;
      if (waiting) waiting.postMessage("SKIP_WAITING");
      reg.addEventListener("updatefound", () => {
        const nextSW = reg.installing;
        if (!nextSW) return;
        nextSW.addEventListener("statechange", () => {
          if (nextSW.state === "installed" && sw.controller) {
            nextSW.postMessage("SKIP_WAITING");
          }
        });
      });
    }).catch(() => {});
  }, []);
  return (
    <div className="App">
      <SyncProvider>
        <LanguageProvider>
          <AuthProvider>
            <PlatformSettingsProvider>
            <BrowserRouter>
              <Toaster richColors position="top-right" />
              <AutoTranslator>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/account-deletion" element={<AccountDeletion />} />
                <Route path="/j/:token" element={<PublicJobStatusPage />} />
                <Route path="/login" element={<Anon><LoginPage /></Anon>} />
                <Route path="/register" element={<Anon><RegisterPage /></Anon>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reactivate" element={<Reactivate />} />
                <Route
                  element={
                    <Protected workshopOnly>
                      <AppShell />
                    </Protected>
                  }
                >
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/customers/:id" element={<CustomerDetailPage />} />
                  <Route path="/vehicles" element={<VehiclesPage />} />
                  <Route path="/job-cards" element={<JobCardsPage />} />
                  <Route path="/job-cards/new" element={<NewJobCardPage />} />
                  <Route path="/job-cards/:id" element={<JobCardDetailPage />} />
                  <Route path="/my-jobs" element={<MyJobsPage />} />
                  <Route path="/inventory" element={<Navigate to="/spare-parts/stock" replace />} />
                  <Route path="/spare-parts" element={<SpareOverview />} />
                  <Route path="/spare-parts/stock" element={<StockList />} />
                  <Route path="/spare-parts/stock/:id" element={<PartDetail />} />
                  <Route path="/spare-parts/vendors" element={<Vendors />} />
                  <Route path="/spare-parts/vendors/:id" element={<VendorDetail />} />
                  <Route path="/spare-parts/reorder" element={<ReorderAlerts />} />
                  <Route path="/spare-parts/purchases" element={<Purchases />} />
                  <Route path="/spare-parts/purchases/new" element={<PurchaseForm />} />
                  <Route path="/spare-parts/purchases/:id" element={<PurchaseDetail />} />
                  <Route path="/spare-parts/purchases/:id/edit" element={<PurchaseForm />} />
                  <Route path="/spare-parts/counter-sales" element={<CounterSales />} />
                  <Route path="/spare-parts/counter-sales/:id" element={<CounterSaleDetail />} />
                  <Route path="/spare-parts/returns" element={<PurchaseReturns />} />
                  <Route path="/spare-parts/returns/:id" element={<PurchaseReturnDetail />} />
                  <Route path="/enquiries" element={<EnquiriesPage />} />
                  <Route path="/parts-catalogue" element={<PartsCatalogue />} />
                  <Route path="/invoices" element={<InvoicesList />} />
                  <Route path="/invoices/new" element={<InvoiceForm />} />
                  <Route path="/invoices/:id" element={<InvoiceDetail />} />
                  <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
                  <Route path="/gate-pass" element={<GatePassList />} />
                  <Route path="/gate-pass/new" element={<GatePassForm />} />
                  <Route path="/gate-pass/:id" element={<GatePassDetail />} />
                  <Route path="/estimates" element={<EstimatesList />} />
                  <Route path="/estimates/new" element={<CreateEstimate />} />
                  <Route path="/estimates/:id" element={<CreateEstimate />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/support" element={<SupportCasesPage />} />
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route path="profile"     element={<GarageProfile />} />
                    <Route path="users"       element={<UsersSettings />} />
                    <Route path="roles"       element={<RolesInfo />} />
                    <Route path="documents"   element={<DocumentSettings />} />
                    <Route path="preferences" element={<Preferences />} />
                    <Route path="change-password" element={<ChangePassword />} />
                    <Route path="delete-account" element={<DeleteAccount />} />
                  </Route>
                </Route>
                <Route
                  element={
                    <Protected adminOnly>
                      <AdminShell />
                    </Protected>
                  }
                >
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/garages" element={<AdminGarages />} />
                  <Route path="/admin/garages/:gid" element={<AdminGarageProfile />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/enquiries" element={<AdminEnquiries />} />
                  <Route path="/admin/enquiries/:eid" element={<AdminEnquiryDetail />} />
                  <Route path="/admin/deletion-requests" element={<AdminDeletionRequests />} />
                  <Route path="/admin/members" element={<AdminMembers />} />
                  <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                  <Route path="/admin/master-data/makes" element={<AdminMakes />} />
                  <Route path="/admin/master-data/models" element={<AdminModels />} />
                  <Route path="/admin/master-data/service-tasks" element={<AdminServiceTasks />} />
                  <Route path="/admin/master-data/spare-parts" element={<AdminSpareParts />} />
                  <Route path="/admin/master-data/part-categories" element={<AdminPartCategories />} />
                  <Route path="/admin/master-data/translations" element={<AdminTranslations />} />
                  <Route path="/admin/support/cases" element={<AdminSupportCases />} />
                  <Route path="/admin/support/cases/:cid" element={<AdminSupportCaseDetail />} />
                  <Route path="/admin/sync-monitor" element={<AdminSyncMonitor />} />
                  <Route path="/admin/change-requests" element={<AdminChangeRequests />} />
                  <Route path="/admin/change-requests/:cid" element={<AdminChangeRequestDetail />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/reports" element={<AdminReports />} />
                  <Route path="/admin/adoption" element={<AdminAdoption />} />
                  <Route path="/admin/master-data/templates" element={<AdminNotificationTemplates />} />
                  <Route path="/admin/job-cards" element={<AdminJobCards />} />
                  <Route path="/admin/job-cards/:id" element={<AdminJobCardDetail />} />
                  <Route path="/admin/inventory" element={<AdminInventory />} />
                  <Route path="/admin/estimates" element={<AdminEstimates />} />
                  <Route path="/admin/estimates/:id" element={<AdminEstimateDetail />} />
                  <Route path="/admin/invoices" element={<AdminInvoices />} />
                  <Route path="/admin/invoices/:id" element={<AdminInvoiceDetail />} />
                  <Route path="/admin/appointments" element={<AdminAppointments />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </AutoTranslator>
            </BrowserRouter>
            </PlatformSettingsProvider>
          </AuthProvider>
        </LanguageProvider>
      </SyncProvider>
    </div>
  );
}

export default App;
